import { createEl, clearChildren } from '../dom.js';
import { highlightInto } from './highlight.js';
import { Stats } from './stats.js';
import { ClearRefinements } from './clear-refinements.js';
import { RefinementList } from './refinement-list.js';
import { Pagination } from './pagination.js';

/**
 * Форматирует целочисленные копейки в человекочитаемую RU-строку цены.
 * Зеркалит форматтер из dropdown.js — оставлен заинлайненным, чтобы не
 * заводить общий helper-файл ради двух вызывающих; стоит вынести, если
 * появится третий вызывающий (согласно правилу surgical-changes из CLAUDE.md).
 */
function formatPrice(kopecks, currency) {
    if (typeof kopecks !== 'number') return '';
    const rubles = Math.round(kopecks / 100);
    const formatted = rubles.toLocaleString('ru-RU');
    return currency === 'USD' ? `$${formatted}` : `${formatted} ₽`;
}

/**
 * Панель inline-layout — рендерится ПОД search-input, а не как
 * absolute-позиционированный overlay. Левый rail: фильтры-refinements (один
 * RefinementList на каждое facetable-поле). Правая колонка: stats + сетка hits +
 * pagination. ClearRefinements всплывает над rail, когда фильтры активны.
 *
 * Подписывается на widget store; эмитит намерения filter / page / clear
 * через колбэки, предоставленные createWidget (так что логикой refetch
 * владеет оркестратор виджета).
 */
export class InlinePanel {
    /**
     * @param {Element} container  `.doka-root` из SearchBox
     * @param {{
     *     onFilterChange: (field: string, value: string, checked: boolean) => void,
     *     onPageChange: (page: number) => void,
     *     onClear: () => void,
     *     onSelectHit: (hit: object, position: number) => void,
     *     perPage: number,
     * }} handlers
     */
    constructor(container, { onFilterChange, onPageChange, onClear, onSelectHit, perPage }) {
        this.handlers = { onFilterChange, onPageChange, onClear, onSelectHit };
        this.perPage = perPage;

        this.el = createEl('div', { className: 'doka-inline-panel' });
        container.appendChild(this.el);

        // Слева: rail с refinements
        this.railEl = createEl('aside', { className: 'doka-inline-rail', 'aria-label': 'Фильтры' });
        // Справа: колонка результатов
        this.mainEl = createEl('div', { className: 'doka-inline-main' });

        // Строка-header (stats + clear)
        const headerEl = createEl('div', { className: 'doka-inline-header' });
        this.stats = new Stats(headerEl);
        this.clear = new ClearRefinements(headerEl, { onClear });
        this.mainEl.appendChild(headerEl);

        // Сетка hits
        this.hitsEl = createEl('div', { className: 'doka-inline-hits' });
        this.mainEl.appendChild(this.hitsEl);

        // Pagination
        this.pagination = new Pagination(this.mainEl, { onPageChange: () => {} });
        // Перепривязка — спека Pagination использует `onChange`, а не `onPageChange`. Корректируем.
        this.pagination.handlers.onChange = (page) => this.handlers.onPageChange(page);

        this.el.appendChild(this.railEl);
        this.el.appendChild(this.mainEl);

        this.refinements = new Map(); // field → RefinementList
        this.lastFacetSignature = '';
    }

    /**
     * @param {object} state  Состояние store с hits, total, tookMs, facets, appliedFilters, page, loading, error, query, schemaFacets
     */
    render(state) {
        if (state.error) {
            clearChildren(this.hitsEl);
            this.hitsEl.appendChild(createEl('div', { className: 'doka-error' }, ['Поиск временно недоступен']));
            return;
        }
        if (state.loading && state.hits.length === 0) {
            // Состояние загрузки при первой загрузке — показываем placeholder со спиннером
            clearChildren(this.hitsEl);
            this.hitsEl.appendChild(createEl('div', { className: 'doka-loading' }, ['Ищем…']));
            return;
        }

        this.stats.update({ total: state.total, tookMs: state.tookMs });
        this.clear.update(hasActiveFilters(state.appliedFilters));
        this.renderRefinements(state);
        this.renderHits(state);

        const totalPages = Math.max(1, Math.ceil((state.total || 0) / this.perPage));
        this.pagination.update({ page: state.page || 1, totalPages });
    }

    renderRefinements(state) {
        // Инициализируем секции refinement при первом рендере или когда меняется
        // список schema facets. Держим инстансы живыми между ре-рендерами, чтобы
        // фокус на checkbox не терялся при обновлении state.
        const schemaFacets = Array.isArray(state.schemaFacets) ? state.schemaFacets : [];
        const sig = schemaFacets.map((f) => `${f.field}:${f.label}`).join('|');
        if (sig !== this.lastFacetSignature) {
            this.refinements.clear();
            clearChildren(this.railEl);
            for (const facet of schemaFacets) {
                const list = new RefinementList(this.railEl, {
                    field: facet.field,
                    label: facet.label,
                    onChange: this.handlers.onFilterChange,
                });
                this.refinements.set(facet.field, list);
            }
            this.lastFacetSignature = sig;
        }

        const facets = state.facets || {};
        const applied = state.appliedFilters || {};
        for (const [field, list] of this.refinements.entries()) {
            const agg = facets[field];
            const buckets = (agg && Array.isArray(agg.buckets)) ? agg.buckets : [];
            const selected = new Set((applied[field] || []).map(String));
            list.update(buckets, selected);
        }
    }

    renderHits(state) {
        clearChildren(this.hitsEl);
        if (state.hits.length === 0) {
            this.hitsEl.appendChild(createEl('div', { className: 'doka-empty' }, ['Ничего не найдено']));
            return;
        }

        state.hits.forEach((hit, idx) => {
            const src = hit.source || {};
            const name = String(src.name ?? hit.id ?? '');
            const url = typeof src.product_url === 'string' ? src.product_url : null;
            const imageUrl = typeof src.image_url === 'string' ? src.image_url : null;
            const price = typeof src.price_amount === 'number'
                ? formatPrice(src.price_amount, src.price_currency)
                : '';

            const title = createEl('div', { className: 'doka-inline-hit-title' });
            highlightInto(title, name, state.query);

            const thumb = imageUrl
                ? createEl('img', { className: 'doka-inline-hit-thumb', src: imageUrl, alt: '', loading: 'lazy' })
                : createEl('div', { className: 'doka-inline-hit-thumb doka-inline-hit-thumb--placeholder' }, [
                    (name.charAt(0) || '?').toUpperCase(),
                ]);

            const meta = createEl('div', { className: 'doka-inline-hit-meta' }, [
                src.brand ? createEl('span', { className: 'doka-inline-hit-brand' }, [String(src.brand)]) : null,
                price ? createEl('span', { className: 'doka-inline-hit-price' }, [price]) : null,
            ]);

            const card = createEl(url ? 'a' : 'div', {
                className: 'doka-inline-hit' + (url ? '' : ' doka-inline-hit--no-url'),
                ...(url ? { href: url, onClick: (e) => {
                    e.preventDefault();
                    this.handlers.onSelectHit(hit, idx + 1);
                } } : {}),
            }, [thumb, title, meta]);

            this.hitsEl.appendChild(card);
        });
    }
}

function hasActiveFilters(applied) {
    if (!applied) return false;
    for (const v of Object.values(applied)) {
        if (Array.isArray(v) && v.length > 0) return true;
    }
    return false;
}
